<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isThanksVisible"
      v-bind="thanks"
      slot="middle"
    />
    <BaseAlert
      v-if="isPaperOut"
      v-bind="noPaper"
      slot="middle"
    />
    <BaseAlert
      v-else-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <Instruction
      v-bind="instruction"
      class="screen__strech screen__offset-right"
    />
  </BaseScreen>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';

import screenMixin from '@/mixins/screen';
import terminalMixin from '@/mixins/terminal';
import Instruction from '@/components/Instruction.vue';

import CONSTANTS from '@/helpers/constants';

const {
  STEP_PAY_SUCCESS,
  STEP_PAY_ERROR,
  ACTION_RETRY,
} = CONSTANTS;

export default {
  name: 'PayInstructionScreen',
  mixins: [screenMixin, terminalMixin],
  components: {
    Instruction,
  },
  data() {
    return {
      isPaperOut: false,
      isTerminalReady: false,
      isSaleRegistrationComplete: false,
    };
  },
  computed: {
    ...mapState('cart', [
      'isOrderPaid',
      'buyer',
      'order',
    ]),
    title() {
      return this.$t('titles.pay');
    },
    pendingText() {
      return this.isTerminalReady
        ? this.$t('pending.pay')
        : this.$t('pending.hardware');
    },
    instruction() {
      return {
        text: this.$t('texts.payInstruction'),
        video: 'pay_instruction.webm',
        hasButton: false,
      };
    },
    thanks() {
      return {
        icon: {
          name: 'success',
          isSuccess: true,
        },
        title: this.$t('titles.orderPaid'),
        text: this.$t('texts.ticketWasSended', { email: this.paymentCustomerInfo }),
        actions: [
          {
            text: this.$t('actions.sendToAnotherEmail'),
            action: () => this.$router.push(this.$router.options.paths.sendTicket),
          },
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
        ],
      };
    },
    error() {
      return {
        title: this.$t('titles.somethingWrong'),
        text: this.$t('texts.pleaseTryAgain'),
        actions: [
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
          {
            text: this.$t('actions.retry'),
            action: this.retryPay,
          },
        ],
      };
    },
    noPaper() {
      return {
        title: this.$t('titles.paperOut'),
        text: this.$t('texts.pleaseContactAdmin'),
        actions: [
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
        ],
      };
    },
    bookingTickets() {
      return this.order.tickets.filter((ticket) => (ticket.status === 'booking')) || [];
    },
    paymentCustomerInfo() {
      return this.buyer?.email;
    },
    paymentSum() {
      return this.bookingTickets.reduce((sum, ticket) => (sum + ticket.price), 0);
    },
    paymentItems() {
      return this.bookingTickets.map(this.prepareSaleItem);
    },
  },
  beforeRouteLeave(to, from, next) {
    // Чистим весь state, если оплата завершена и переход вне sendTicket или когда ошибка
    const shouldClear = (this.isPaperOut || this.isErrorVisible)
      || (this.isThanksVisible && to.path !== this.$router.options.paths.sendTicket);

    if (shouldClear) {
      this.clearSession();
    }
    next();
  },
  created() {
    this.showPending();
    this.initActions();
    this.startTerminalSaleProcess();
  },
  methods: {
    ...mapActions([
      'clearSession',
    ]),
    ...mapActions('cart', [
      'confirmOrderPayment',
    ]),
    ...mapMutations('cart', [
      'setOrderPayStatus',
    ]),
    prepareSaleItem(item) {
      return {
        name: `${this.$t('fields.ticket')}: №${item.id}`,
        price: item.price,
        quantity: 1,
        sum: item.price,
        discount: 0,
        tax_type: 7,
      };
    },
    onTerminalError() {
      this.showError();
    },
    onTerminalPaperOut() {
      this.isPaperOut = true;
    },
    onTerminalCheckComplete() {
      this.isTerminalReady = true;
      this.hidePending();
    },
    onSaleRegistrationComplete() {
      this.isSaleRegistrationComplete = true;
      this.confirmPayment();
    },
    async confirmPayment() {
      // Подтверждаем оплату
      try {
        this.showPending();

        if (!this.isOrderPaid) {
          await this.confirmOrderPayment({
            order: this.order,
            amount: this.paymentSum,
          });
          this.setOrderPayStatus(true);
        }

        this.showThanks();
      } catch (e) {
        console.error('confirmPayment: ', e);
        this.showError();
      }
    },
    retryPay() {
      if (!this.isErrorVisible) return;
      this.showPending();
      this.hideError();
      const retryAction = this.isSaleRegistrationComplete
        ? this.onSaleRegistrationComplete
        : this.startTerminalSaleProcess;
      retryAction();
    },
    sayError() {
      this.$robot.dialogService.setCase(STEP_PAY_ERROR, true, true);
      this.$robot.dialogService.sayReplicByName(STEP_PAY_ERROR, true);
    },
    sayThanks() {
      this.$robot.dialogService.setCase(STEP_PAY_SUCCESS, true, true);
      this.$robot.dialogService.sayReplicByName(STEP_PAY_SUCCESS, true);
    },
    initActions() {
      const rootActions = [
        [ACTION_RETRY, this.retryPay],
      ];

      rootActions.forEach((action) => this.$root.$on(...action));

      this.$once('hook:beforeDestroy', () => {
        rootActions.forEach((action) => this.$root.$off(...action));
      });
    },
  },
};
</script>
